import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import isLeapYear from 'dayjs/plugin/isLeapYear';

dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear); // dependency for isoWeeksInYear

@Injectable({
  providedIn: 'root',
})
export class IsoWeekService {
  private _currentDate: dayjs.Dayjs = dayjs();

  setCurrentDate(date: dayjs.Dayjs): void {
    this._currentDate = date;
  }

  private _getIsoWeek(date: dayjs.Dayjs): string {
    const year: number = date.isoWeekYear();
    const week: number = date.isoWeek();
    return `${year}-W${week.toString().padStart(2, '0')}`;
  }

  private _parseWeek(isoWeekStr: string): { year: number; week: number } {
    const match: RegExpMatchArray = isoWeekStr.match(/^(\d{4})-W(\d{2})$/)!;
    return { year: parseInt(match[1]), week: parseInt(match[2]) };
  }

  get firstYearWeek(): string {
    return this._getIsoWeek(this._currentDate.startOf('year'));
  }

  get currentWeek(): string {
    return this._getIsoWeek(this._currentDate);
  }

  previousWeek(weeksToSubtract: number = 1): string {
    const targetDate: dayjs.Dayjs = this._currentDate.subtract(
      weeksToSubtract,
      'week',
    );
    return this._getIsoWeek(targetDate);
  }

  nextWeek(weeksToAdd: number = 1): string {
    const targetDate: dayjs.Dayjs = this._currentDate.add(weeksToAdd, 'week');
    return this._getIsoWeek(targetDate);
  }

  // e.g. new Date('2025-03-10T14:30:00Z') => 2025-03-10T23:59:59.999Z
  utcEndOfDay(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        23,
        59,
        59,
        999,
      ),
    );
  }

  // e.g. new Date('2025-03-10T14:30:00Z') => 2025-03-10T00:00:00.000Z
  utcStartOfDay(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0,
      ),
    );
  }

  /**
   * Returns true iff `isoWeekStr` is a syntactically valid ISO week string
   * (`YYYY-Www`) AND its week number is within that year's bounds
   * (1–52 or 1–53 depending on the year). The single check most callers want.
   */
  public isValidWeek(isoWeekStr: string): boolean {
    if (!this._isValidWeekFormat(isoWeekStr)) {
      return false;
    }

    const { year, week } = this._parseWeek(isoWeekStr);
    return this._isValidWeekNumber(year, week);
  }

  private _isValidWeekFormat(isoWeekStr: string): boolean {
    return /^(\d{4})-W(\d{2})$/.test(isoWeekStr);
  }

  private _isValidWeekNumber(year: number, week: number): boolean {
    // ISO week numbers are from 1-53, but some years only have 52 weeks
    const maxWeeks: number = dayjs().year(year).isoWeeksInYear();
    return week >= 1 && week <= maxWeeks;
  }

  startDateOfWeek(isoWeekStr: string): Date | null {
    if (!this.isValidWeek(isoWeekStr)) {
      return null;
    }

    const { year, week } = this._parseWeek(isoWeekStr);
    // Anchoring to June 1 keeps us safely inside the ISO week year so that
    // isoWeek() sets the correct week without year-boundary ambiguity.
    const monday: dayjs.Dayjs = dayjs(`${year}-06-01`)
      .isoWeek(week)
      .startOf('isoWeek');
    return new Date(Date.UTC(monday.year(), monday.month(), monday.date()));
  }

  endDateOfWeek(isoWeekStr: string): Date | null {
    if (!this.isValidWeek(isoWeekStr)) {
      return null;
    }

    const { year, week } = this._parseWeek(isoWeekStr);
    // Anchoring to June 1 keeps us safely inside the ISO week year so that
    // isoWeek() sets the correct week without year-boundary ambiguity.
    const sunday: dayjs.Dayjs = dayjs(`${year}-06-01`)
      .isoWeek(week)
      .endOf('isoWeek');
    return new Date(
      Date.UTC(sunday.year(), sunday.month(), sunday.date(), 23, 59, 59, 999),
    );
  }

  getWeekDifference(startWeekStr: string, endWeekStr: string): number {
    if (!startWeekStr || !endWeekStr) {
      throw new Error('Week parameters are required');
    }

    if (
      !this._isValidWeekFormat(startWeekStr) ||
      !this._isValidWeekFormat(endWeekStr)
    ) {
      throw new Error('Week parameters must be in the format [YYYY]-W[WW]');
    }

    // Parse both strings into { year, week } components.
    const { year: startYear, week: startWeek } = this._parseWeek(startWeekStr);
    const { year: endYear, week: endWeek } = this._parseWeek(endWeekStr);

    // Validate the week numbers (format was checked above, but e.g. W53 is only
    // valid in years that actually have 53 weeks).
    if (
      !this._isValidWeekNumber(startYear, startWeek) ||
      !this._isValidWeekNumber(endYear, endWeek)
    ) {
      throw new Error('Invalid week number for year');
    }

    // Create dayjs objects anchored to June 1 of the respective year so that
    // isoWeek() places us in the correct ISO week year without boundary issues.
    const startDate: dayjs.Dayjs = dayjs(`${startYear}-06-01`)
      .isoWeek(startWeek)
      .startOf('isoWeek');

    const endDate: dayjs.Dayjs = dayjs(`${endYear}-06-01`)
      .isoWeek(endWeek)
      .startOf('isoWeek');

    // Calculate the difference in whole weeks.
    return endDate.diff(startDate, 'week');
  }
}
